export type ScreenId = "splash" | "home" | "select" | "game";

export interface Screen {
  readonly id: ScreenId;
  render(): HTMLElement;
  onEnter?(): void;
  onExit?(): void;
}

interface ScreenRecord {
  screen: Screen;
  element: HTMLElement;
}

const TRANSITION_MS = 340;

export class ScreenManager {
  private records = new Map<ScreenId, ScreenRecord>();
  private current: ScreenId | null = null;
  private transitioning = false;
  private pendingId: ScreenId | null = null;
  public onChange: ((id: ScreenId) => void) | null = null;

  constructor(private root: HTMLElement) {}

  register(screen: Screen): void {
    if (this.records.has(screen.id)) return;
    const element = screen.render();
    element.classList.add("screen");
    this.root.appendChild(element);
    this.records.set(screen.id, { screen, element });
  }

  /**
   * Replaces an already-registered screen with a fresh instance (new DOM).
   * The new screen is rendered FIRST — if rendering throws, the old screen
   * stays intact instead of leaving a detached, invisible record behind.
   */
  replace(screen: Screen): void {
    const element = screen.render();
    element.classList.add("screen");

    const existing = this.records.get(screen.id);
    if (existing) {
      try {
        existing.screen.onExit?.();
      } catch (err) {
        console.error(`Screen "${screen.id}" failed to exit:`, err);
      }
      existing.element.remove();
      if (this.current === screen.id) this.current = null;
    }

    this.root.appendChild(element);
    this.records.set(screen.id, { screen, element });
  }

  go(id: ScreenId): void {
    // A navigation arriving mid-transition is QUEUED, never dropped —
    // dropped navigations left users stranded on blank screens.
    if (this.transitioning) {
      this.pendingId = id;
      return;
    }
    if (id === this.current) return;

    const next = this.records.get(id);
    if (!next) throw new Error(`Unknown screen: ${id}`);
    this.transitioning = true;

    // Self-heal: a fast replace() during a transition can leave the
    // record pointing at a detached element — reattach before showing.
    if (!next.element.isConnected) this.root.appendChild(next.element);
    next.element.classList.remove("is-leaving");

    const prev = this.current !== null ? this.records.get(this.current) : undefined;
    this.current = id;
    try { this.onChange?.(id); } catch {}

    try {
      next.screen.onEnter?.();
    } catch (err) {
      console.error(`Screen "${id}" failed to enter:`, err);
    }
    requestAnimationFrame(() => next.element.classList.add("is-active"));
    if (prev) {
      try {
        prev.screen.onExit?.();
      } catch (err) {
        console.error(`Screen "${prev.screen.id}" failed to exit:`, err);
      }
      prev.element.classList.add("is-leaving");
      prev.element.classList.remove("is-active");
      window.setTimeout(() => prev.element.classList.remove("is-leaving"), TRANSITION_MS);
    }

    window.setTimeout(() => {
      this.transitioning = false;
      if (this.pendingId) {
        const nextId = this.pendingId;
        this.pendingId = null;
        if (nextId !== this.current) this.go(nextId);
      }
    }, TRANSITION_MS);
  }

  get active(): ScreenId | null {
    return this.current;
  }
}
